// filepath: apps/web/src/features/admin/hmacKeys/OneTimeCopyDialog.tsx
//
// FR-UI-ADM-7 AC-4 -- one-time copy affordance for the new key value.
//
// INV-5 contract: the new key value is rendered EXACTLY ONCE here.
// The dialog stores the value in React state only (no localStorage).
// On unmount, the state is cleared.
//
// The viewer also writes the value to sessionStorage under
// `webhook:rotate:newKeyValue` so that a tab refresh does not
// silently lose the value (mirrors FR-UI-ADM-10 AC-2 pattern).
// On unmount, the sessionStorage entry is removed.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const SESSION_KEY = "webhook:rotate:newKeyValue";

interface OneTimeCopyDialogProps {
  keyValue: string;
  keyId: string;
  kind: "issue" | "rotate";
  onClose: () => void;
}

export function OneTimeCopyDialog({
  keyValue,
  keyId,
  kind,
  onClose,
}: OneTimeCopyDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [cleared, setCleared] = useState(false);

  // Persist to sessionStorage on mount (mirrors FR-UI-ADM-10 AC-2).
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, keyValue);
    } catch {
      // sessionStorage may be unavailable (e.g. private mode); tolerate.
    }
    return () => {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch {
        // tolerate
      }
    };
  }, [keyValue]);

  const doCopy = async () => {
    try {
      // navigator.clipboard requires a secure context in most browsers.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(keyValue);
      } else {
        // Fallback: textarea + execCommand("copy").
        const ta = document.createElement("textarea");
        ta.value = keyValue;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const doClose = () => {
    setCleared(true);
    onClose();
  };

  return (
    <div
      data-testid="one-time-copy-dialog"
      className="hmac-keys-one-time-copy"
      role="dialog"
      aria-modal="true"
    >
      <h2>{t("admin.webhooks.keys.one_time_copy_title")}</h2>
      <p data-testid="one-time-copy-warning">
        {t("admin.webhooks.keys.one_time_copy_warning")}
      </p>

      <p>
        <strong>{t("admin.webhooks.keys.key_id_label")}:</strong>{" "}
        <code data-testid="one-time-copy-key-id">{keyId}</code>
      </p>

      {cleared ? (
        <p data-testid="one-time-copy-cleared">
          {t("admin.webhooks.keys.value_cleared_message")}
        </p>
      ) : (
        <pre
          data-testid="one-time-copy-value"
          className="hmac-keys-one-time-copy__value"
          aria-label="new key value"
        >
          {keyValue}
        </pre>
      )}

      <div className="hmac-keys-one-time-copy__actions">
        <button
          type="button"
          onClick={doCopy}
          disabled={cleared}
          data-testid="one-time-copy-copy-button"
        >
          {copied
            ? t("admin.webhooks.keys.copy_button") + " ✓"
            : t("admin.webhooks.keys.copy_button")}
        </button>
        <button
          type="button"
          onClick={doClose}
          data-testid="one-time-copy-done-button"
        >
          {t("admin.webhooks.keys.done_button")}
        </button>
      </div>

      <small data-testid="one-time-copy-kind">kind: {kind}</small>
    </div>
  );
}
